import React, {Component, ReactElement, ReactNode} from 'react'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Messages} from '../../../Messages'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import NatAreaCodeRepo from './repository/NatAreaCodeRepo'
import {AreaCodeInfo, NatGetAreaCode} from './repository/NatGetAreaCode'
import {NatDeleteAreaCode} from './repository/NatDeleteAreaCode'
import {Location} from 'react-router-dom'

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface Props {
    areaId: number
    areaCodeRepo: NatAreaCodeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location<LocationState>
}

interface States {
    areaCodeInfo: AreaCodeInfo
    isLoadCompleted: boolean
}


class NatAdminAreaCodePage extends Component<Props & AdminPagePropsSet, States> {

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            areaCodeInfo: {
                areaId: -1,
                areaCode: '',
                areaName: '',
                contactMailAddress: '',
                version: 0,
            },
            isLoadCompleted: false,
        }
        this.backAreaCodeListLinkClick = this.backAreaCodeListLinkClick.bind(this)
        this.editAreaCodeLinkClick = this.editAreaCodeLinkClick.bind(this)
        this.deleteAreaCode = this.deleteAreaCode.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            // 地域コード情報取得
            const response: Nullable<NatGetAreaCode> = await this.props.asyncCallFunction(
                this.props.areaCodeRepo.getAreaCode.bind(
                    this.props.areaCodeRepo, this.props.areaId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response && response.details) {
                this.setState({areaCodeInfo: response.details})
            }
            this.showInitialMessage()
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    backAreaCodeListLinkClick(): void {
        this.props.navigate(`/admin/areaCodeList`)
    }

    editAreaCodeLinkClick(): void {
        this.props.navigate(`/admin/areaCodeEdit/${this.props.areaId}`)
    }

    // 削除ボタン押下
    async deleteAreaCode(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString('地域コード'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkClick,
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleDeleteOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatDeleteAreaCode> = await this.props.asyncCallFunction(
                this.props.areaCodeRepo.deleteAreaCode.bind(this.props.areaCodeRepo, this.props.areaId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response && response.requestError === null) {
                this.props.navigate(`/admin/areaCodeList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0009.toString('地域コード'),
                            },
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }


    renderDetailsTable(): ReactNode {
        return (
            <>
                <Table>
                    <StyledTableCaption>地域コード情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>地域コード</TableRowName>
                            <TableCell>{this.state.areaCodeInfo.areaCode}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>地域名</TableRowName>
                            <TableCell>{this.state.areaCodeInfo.areaName}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>連絡先メールアドレス</TableRowName>
                            <TableCell>{this.state.areaCodeInfo.contactMailAddress}</TableCell>
                        </TableRow>
                    </TableBody>

                </Table>
            </>
        )
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        return (
            <>
                <PageFotterAreaRight>
                    <DeleteLink
                        onClick={this.deleteAreaCode}>
                        地域コードを削除する
                    </DeleteLink>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <AdminPrimaryButton
                        onClick={this.editAreaCodeLinkClick}>
                        編集する
                    </AdminPrimaryButton>
                </PageFotterAreaLeft>
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/areaCodeList`}
                    >
                        地域コード一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_AREA_CODE}>
                    <Box mb={'15px'}>
                        <PageFooterArea>
                            {this.renderPageHeaderArea()}
                        </PageFooterArea>
                    </Box>
                </AuthorityChecker>
                <PageMainArea>
                    {this.renderDetailsTable()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminAreaCodePage)

